<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3">
      <SimplePageHeader title="Task Management" class="px-4 pr-5 mb-5">
        <template v-slot:right-buttons>
          <ButtonCustomIcon
            v-if="!isReadOnlyAccess"
            icon="add"
            color="primary"
            text="Add"
            :routeName="userAdminTodoEditRoute.name"
            :block="true"
          />
        </template>
      </SimplePageHeader>
      <TodoFilters
        class="px-5 mb-sm-5"
        @search="handleSearchClick"
        @clear="handleSearchClick"
      ></TodoFilters>
    </div>
    <v-container fluid class="px-0 pt-2 mt-4">
      <v-layout column>
        <v-data-table
          dense
          :headers="headers"
          :items="todoList"
          class="elevation-2"
        >
          <template v-slot:item.targetDate="{ item }">
            <span :class="{
                    'past-due' : new Date(item.targetDate) < new Date() && item.complete === false,
                    'for-today': new Date(item.targetDate).setHours(0, 0, 0, 0) ===
                    new Date().setHours(0, 0, 0, 0) &&
                    item.complete === false,
                    'for-tomorrow': isForTomorrow(item.targetDate),
                    'complete': item.complete === true,
                    'pending': new Date(item.targetDate) > new Date() && item.complete === false && !isForTomorrow(item.targetDate)
                  }"
            >{{ item.targetDate | formatDate }}</span>
          </template>
          <template v-slot:item.complete="{ item }">
            {{ item.complete | boolToYesNo }}
          </template>
          <template v-slot:item.completedAt="{ item }">
            {{ item.completedAt | formatDate }}
          </template>
          <template v-slot:item.createdAt="{ item }">
            {{ item.createdAt | formatDate }}
          </template>
          <template v-slot:item.action="{ item }">
            <v-btn
              class="mx-1 my-1"
              color="primary"
              outlined
              @click.prevent="editTodo($event, item)"
            >
              <v-icon color="primary">{{ editButtonIcon }}</v-icon>
            </v-btn>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-if="!item.complete && !isReadOnlyAccess"
                  class="mx-1 my-1"
                  color="green"
                  outlined
                  v-bind="attrs"
                  v-on="on"
                  @click="completeTodoHandler(item)"
                >
                  <v-icon color="green">check</v-icon>
                </v-btn>
              </template>
              <span>Mark as Completed</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-layout>
    </v-container>
    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import TodoFilters from './TodoFilters'
import { userAdminTodoEditRoute } from '@/lib/constants/routes'
import { ButtonAdd, BottomSpacer, ButtonCustomIcon } from '@/components/buttons'
import { SimplePageHeader } from '@/components/pageComponents'
import getDueDateColor from '@/lib/display/getCompletedDateColor'
import { ADMIN, MANAGER } from '@/lib/constants/securityRoles'
import { userHasRoles, getCurrentUser } from '@/lib/authentication'

export default {
  components: {
    TodoFilters,
    ButtonAdd,
    BottomSpacer,
    ButtonCustomIcon,
    SimplePageHeader
  },
  data () {
    return {
      userAdminTodoEditRoute,
      headers: [
        { text: 'Name', value: 'name', sortable: false, width: '30%' },
        { text: 'Assigned to', value: 'fullName', sortable: false, width: '20%' },
        { text: 'Created On', value: 'createdAt', align: 'center', sortable: false, width: '10%', class: 'px-0', cellClass: 'px-2' },
        { text: 'Target Date', value: 'targetDate', align: 'center', width: '10%', class: 'px-0', cellClass: 'px-2' },
        { text: 'Completed On', value: 'completedAt', align: 'center', sortable: false, width: '10%', class: 'px-0', cellClass: 'px-2' },
        { text: 'Actions', value: 'action', align: 'center', sortable: false, width: '20%', class: 'px-0', cellClass: 'px-2' }
      ],
      showModal: false,
      modalTitle: '',
      currentUserID: 0,
      todoInfo: {}
    }
  },
  computed: {
    ...mapFields('todos', [
      'todoList'
    ]),
    isReadOnlyAccess: {
      get () {
        return !this.userHasRoles({ user: this.getCurrentUser(), allowedRoles: [ADMIN, MANAGER] })
      }
    },
    editButtonIcon () {
      return this.isReadOnlyAccess ? 'visibility' : 'edit'
    }
  },
  methods: {
    getDueDateColor,
    ...mapActions('todos', [
      'getTodosWithFilters',
      'completeTodo'
    ]),
    ...mapActions('todoFilters', [
      'clearFilters'
    ]),
    editTodo (event, todo) {
      if (event.ctrlKey === true) {
        let route = this.$router.resolve({ name: userAdminTodoEditRoute.name, params: { id: todo.id } })
        window.open(route.href, '_blank')
      } else {
        this.$router.push({ name: userAdminTodoEditRoute.name, params: { id: todo.id } })
      }
    },
    handleSearchClick () {
      this.getTodosWithFilters()
    },
    completeTodoHandler (todo) {
      todo.complete = true
      this.completeTodo(todo)
    },
    closeDialog () {
      this.showModal = false
    },
    getCurrentUser,
    userHasRoles,
    isForTomorrow (targetDate) {
      let completedAt = new Date()
      const tomorrowDueDate = new Date(targetDate).setHours(0, 0, 0, 0)
      const tomorrowDate = new Date(new Date(completedAt).getFullYear(), new Date(completedAt).getMonth(), new Date(completedAt).getDate(completedAt) + 1).setHours(0, 0, 0, 0)
      return tomorrowDate === tomorrowDueDate
    }
  },
  created () {
    this.getTodosWithFilters()
  }
}
</script>

<style>
.past-due {
  color: white;
  border: 1px solid red;
  background-color: red;
  border-radius: 10px;
  padding: 1px 15px 1px 15px;
  display: block;
  text-align: center;
}

.for-today {
  color: black;
  letter-spacing: 1px;
  border: 1px solid orange;
  background-color: orange;
  border-radius: 10px;
  padding: 1px 15px 1px 15px;
  display: block;
  text-align: center;
}

.for-tomorrow {
  color: black;
  border: 1px solid yellow;
  background-color: yellow;
  border-radius: 10px;
  padding: 1px 15px 1px 15px;
  display: block;
  text-align: center;
}

.pending {
  color: blue;
  border: 1px solid blue;
  border-radius: 10px;
  padding: 1px 15px 1px 15px;
  display: block;
  text-align: center;
}

.complete {
  color: rgb(64, 153, 47);
  border: 1px solid rgb(64, 153, 47);
  border-radius: 10px;
  padding: 1px 15px 1px 15px;
  display: block;
  text-align: center;
}
</style>
